import { useEffect } from "react";
import { useTasks, Task, TaskStatus } from "@/context/TasksContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ClipboardList, Clock, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useIngestJob } from "@/hooks/use-ingest-jobs";
import { describeIngestJob } from "@/services/ingest-jobs.service";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRealJobId = (jobId: string) => UUID_RE.test(jobId);

function getStatusIcon(status: TaskStatus) {
  switch (status) {
    case "pending":
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case "running":
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case "errored":
      return <AlertCircle className="h-4 w-4 text-red-500" />;
  }
}

function getStatusBadge(status: TaskStatus) {
  const variants: Record<TaskStatus, string> = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    running: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    errored: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <Badge variant="outline" className={variants[status]}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export default function TasksPage() {
  const { tasks } = useTasks();

  // Sort tasks by createdAt (newest first)
  const sortedTasks = [...tasks].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <main className="flex-1 space-y-6 p-2">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
          Tasks
        </h2>
        <p className="text-muted-foreground">
          Monitor your product import tasks
        </p>
      </div>

      {/* Task List */}
      <div className="space-y-4">
        {sortedTasks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ClipboardList className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500">No tasks found</p>
            </CardContent>
          </Card>
        ) : (
          sortedTasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))
        )}
      </div>
    </main>
  );
}

function TaskCard({ task }: { task: Task }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              {getStatusIcon(task.status)}
              <span className="font-mono text-sm text-gray-600">{task.jobId}</span>
              {getStatusBadge(task.status)}
            </div>
            
            <div className="text-sm">
              <span className="font-medium">{task.catalogueName}</span>
              <span className="text-gray-500 mx-2">•</span>
              <span className="text-gray-600">
                {task.type === "file-upload" ? "File Upload" : "URL Import"}
              </span>
            </div>

            {/* File-upload phase (before ingestion starts) */}
            {task.status === "running" &&
              task.type === "file-upload" &&
              typeof task.progress === "number" &&
              task.progress < 100 && (
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Uploading</span>
                    <span className="font-medium">{task.progress}%</span>
                  </div>
                  <Progress value={task.progress || 0} className="h-2" />
                </div>
              )}

            {/* Live ingestion progress once the job has been handed off */}
            {isRealJobId(task.jobId) && <IngestionTaskProgress task={task} />}

            {task.error && (
              <p className="text-sm text-red-600 mt-2">{task.error}</p>
            )}

            <p className="text-xs text-gray-400">
              Started {formatDistanceToNow(task.createdAt, { addSuffix: true })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Polls the real ingestion job and keeps the task's status in sync so the badge
// reflects true completion/failure (the modal no longer fakes completion).
function IngestionTaskProgress({ task }: { task: Task }) {
  const { updateTask } = useTasks();
  const { job } = useIngestJob(task.catalogueId, task.jobId);

  const jobStatus = job?.status;
  useEffect(() => {
    if (!job) return;
    const mapped: TaskStatus =
      job.status === "completed" ? "completed" : job.status === "failed" ? "errored" : "running";
    if (mapped !== task.status) {
      updateTask(task.id, { status: mapped, error: job.error ?? job.lastError ?? undefined });
    }
    // Re-run only when the underlying job status changes.
  }, [jobStatus]);

  if (!job) return null;
  const display = describeIngestJob(job);

  return (
    <div className="space-y-1 pt-2">
      {display.percent != null ? (
        <>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{display.label}</span>
            <span className="font-medium">{display.percent}%</span>
          </div>
          <Progress value={display.percent} className="h-2" />
        </>
      ) : (
        <div className="text-sm text-gray-600">{display.label}…</div>
      )}
      {display.detail && <p className="text-xs text-gray-500">{display.detail}</p>}
    </div>
  );
}
